import { each, pipe, reject } from '@fxts/core';
import { $, html, on, rune, View } from 'rune-ts';
import { ConfigSentryF } from '../../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { pushLoginStack } from '../../../../../../../modules/Creator/Login/F/fs';
import { MarketplaceAuthF } from '../../../../../../../modules/Marketplace/Auth/F/Function/module/MarketplaceAuthF';
import { MShopUtilF } from '../../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { BannerTypeS } from '../../../../../features/Banner/outbound/share';
import { PlusCrewApiS } from '../../../../../features/PlusCrew/outbound/share';
import { makeEmptyScreenNavigate } from '../../../../../shared/app/navigate';
import { Locals } from '../../../../../shared/type/locals';
import { staticTypoPcMo, typoPcMo } from '../../../../../shared/typography/typo';
import { throttleWithLastExecute } from '../../../../../shared/util';
import { dataStr } from '../../../../../shared/util/dataStr';
import { getResizedUrl } from '../../../../../shared/util/image';
import { preventEscape } from '../../../../../shared/util/preventEscape';
import { getSharedData } from '../../../../../shared/util/rune';
import { makePlusUrl } from '../../../../../shared/util/url';
import { ButtonArrow } from '../../../../components/atoms/ButtonArrow/ButtonArrow';
import { ButtonHeart } from '../../../../components/atoms/ButtonHeart/ButtonHeart';
import {
  AfreecaIcon,
  BlogIcon,
  ChzzkIcon,
  FacebookIcon,
  InstagramIcon,
  OfficialIcon,
  TiktokIcon,
  XIcon,
  YoutubeIcon,
} from '../../../../components/atoms/Icon';
import { Confirm } from '../../../../components/cells/Confirm/Confirm';
import { SwiperChangeEvent, SwiperView } from '../../../../components/cells/Swiper/Swiper';
import { Video, VideoActiveEvent } from '../../../../components/cells/Video/Video';
import klass from './PlusBanner.module.scss';

export interface PlusBannerProps {
  plus_crew: Locals['plus_crew'];
  banners: BannerTypeS.PlusBannerData[];
}

interface PlusBannerPaginationData {
  current_index: number;
  max_index: number;
}

interface PlusBannerNavigationData {
  prev_enable: boolean;
  next_enable: boolean;
}

class PlusBannerInformationView extends View<Locals['plus_crew']> {
  override template() {
    const { name, is_liked, like_count, brand_title_default, brand_title, brand_desc } = this.data;

    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.information}">
        <div class="${klass.contents}">
          <span class="${staticTypoPcMo({ is_mobile, mo: 'unica_12_medium', pc: 'unica_16_medium' })}"
            >${brand_title_default && ET.lang !== 'jp'
              ? html.preventEscape(brand_title_default.toUpperCase())
              : html.preventEscape(brand_title || name)}</span
          >
          <h3 class="${typoPcMo({ is_mobile, pc: '48_bold', mo: '28_bold' })}">
            ${html.preventEscape(brand_title || name)}
          </h3>
          ${!brand_desc
            ? ''
            : html`<span class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}"
                >${html.preventEscape(brand_desc)}</span
              >`}
          <div class="${klass.icon_wrapper}">${this.makeLinks()}</div>
          ${new ButtonHeart({ count: like_count, is_active: is_liked, size: 'medium', type: 'dark' })}
        </div>
      </div>
    `;
  }

  makeLinks() {
    const {
      fb_url,
      tw_url,
      yt_url,
      ig_url,
      official_url,
      naver_blog_url,
      tiktok_url,
      afreecatv_url,
      chzzk_url,
    } = this.data;

    return html`
      ${official_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(official_url))}"
            href="${official_url}"
            target="_blank"
          >
            ${OfficialIcon}
          </a>`
        : ''}
      ${yt_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(yt_url))}"
            href="${yt_url}"
            target="_blank"
          >
            ${YoutubeIcon}
          </a>`
        : ''}
      ${naver_blog_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(naver_blog_url))}"
            href="${naver_blog_url}"
            target="_blank"
          >
            ${BlogIcon}
          </a>`
        : ''}
      ${ig_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(ig_url))}"
            href="${ig_url}"
            target="_blank"
          >
            ${InstagramIcon}
          </a>`
        : ''}
      ${tw_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(tw_url))}"
            href="${tw_url}"
            target="_blank"
          >
            ${XIcon}
          </a>`
        : ''}
      ${fb_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(fb_url))}"
            href="${fb_url}"
            target="_blank"
          >
            ${FacebookIcon}
          </a>`
        : ''}
      ${tiktok_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(tiktok_url))}"
            href="${tiktok_url}"
            target="_blank"
          >
            ${TiktokIcon}
          </a>`
        : ''}
      ${afreecatv_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(afreecatv_url))}"
            href="${afreecatv_url}"
            target="_blank"
          >
            ${AfreecaIcon}
          </a>`
        : ''}
      ${chzzk_url
        ? html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeEmptyScreenNavigate(chzzk_url))}"
            href="${chzzk_url}"
            target="_blank"
          >
            ${ChzzkIcon}
          </a>`
        : ''}
    `;
  }

  protected override onRender() {
    this.delegate('click', ButtonHeart, async (_e, view) => {
      if (!MarketplaceAuthF.isSignedIn()) {
        const confirmed = (await Confirm.open({
          title: ET('mps2::login::need_login::title'),
          message: preventEscape(ET('mps2::login::need_login::message')),
          confirmText: ET('mps2::login::login'),
          cancelText: ET('mps2::signup::close'),
        })) as boolean;

        if (confirmed) {
          if (MShopUtilF.isApp()) {
            pushLoginStack();
          } else {
            location.href = `/${T.lang}/@/login?url=${location.pathname + location.search}`;
          }
        }
        return;
      }

      const prev_like = this.data.is_liked;

      this.data.like_count = this.data.like_count + (this.data.is_liked ? -1 : 1);
      view.data.count = this.data.like_count;
      this.data.is_liked = !this.data.is_liked;
      view.data.is_active = this.data.is_liked;
      view.redraw();

      try {
        await this.throttleRequestLike(prev_like);
      } catch (e) {
        ConfigSentryF.error(e);
      }
    });
  }

  throttleRequestLike = throttleWithLastExecute(async (is_like: boolean) => {
    await PlusCrewApiS.postPlusLike({
      crew_id: this.data.id,
      already_like: is_like,
    });
  }, 500);
}

class PlusBannerItemView extends View<BannerTypeS.PlusBannerData> {
  video?: Video;

  constructor(
    data: BannerTypeS.PlusBannerData,
    public plus_crew: Locals['plus_crew'],
    public first_id: number,
    public slide_paused = true,
  ) {
    super(data);
  }

  protected override template() {
    return html` <div class="${klass.item_wrapper}">${this.bannerItem()}</div> `;
  }

  bannerItem() {
    const {
      id,
      $many: {
        crew_banner_contents: [content],
      },
    } = this.data;
    if (content) {
      const { format, content_url, thumbnail_url } = content;

      if ((format === 'video' || format === 'youtube') && content_url) {
        this.video = new Video(
          { src: content_url, thumbnail: thumbnail_url, id },
          { format, autoplay: id === this.first_id, klass: klass.item },
        );

        return this.video;
      }
    }

    return this.makeImageTemplate();
  }

  makeImageTemplate() {
    const {
      id,
      url,
      current_window,
      $many: {
        crew_banner_contents: [content],
      },
    } = this.data;

    if (content) {
      const { format, content_url } = content;
      const is_mobile = !!getSharedData(this)?.is_mobile;
      const target = url?.includes('http') ? '_blank' : current_window ? '_self' : '_blank';
      const plus_url = url?.includes('http')
        ? url
        : url
        ? makePlusUrl(this.plus_crew.domain_name, url)
        : null;

      return html`
      <div data-id="${id}" data-format="${format}" class="${klass.item}">
        <${plus_url ? 'a' : 'div'}
          ${url ? html`target="${target}"` : ''}
          ${url ? html`href="${url}"` : ''}
          ${plus_url ? html`data-post-message="${dataStr(makeEmptyScreenNavigate(plus_url))}"` : ''}
          class="${klass.container}"
        >
          ${
            content_url
              ? html`<img
                  src="${getResizedUrl({
                    url: content_url,
                    format: 'webp',
                    width: is_mobile ? 750 : 1500,
                  })}"
                  alt="thumbnail"
                />`
              : ''
          }
        </${plus_url ? 'a' : 'div'}>
      </div>
    `;
    }
  }
}

class PlusBannerNavigationView extends View<PlusBannerNavigationData> {
  override template() {
    const navigation = this.data;
    return html`<div class="navigation ${klass.navigation}">
      ${navigation.prev_enable ? new ButtonArrow({ direction: 'left', type: 'bright' }) : ''}
      ${navigation.next_enable ? new ButtonArrow({ direction: 'right', type: 'bright' }) : ''}
    </div>`;
  }

  protected override onMount() {
    this.delegate('click', ButtonArrow, (_e, targetView) => {
      this.dispatchEvent(NavigateHandleEvent, {
        bubbles: true,
        detail: {
          direction: targetView.data.direction === 'left' ? 'prev' : 'next',
        },
      });
    });
  }
}

class NavigateHandleEvent extends CustomEvent<{
  direction: 'prev' | 'next';
}> {}

class PlusBannerPaginationView extends View<PlusBannerPaginationData> {
  protected override template() {
    const pagination = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`<div
      class="pagination ${klass.pagination} ${staticTypoPcMo({
        is_mobile,
        mo: 'bebas_14_bold',
        pc: 'bebas_16_bold',
      })}"
    >
      <span class="current_index"> ${this.calcIndex(pagination.current_index)} </span>
      <div class="${klass.pages}">
        <div class="progress ${klass.page}" style="width: ${this.calcProgress()}%;"></div>
      </div>
      <span> ${this.calcIndex(pagination.max_index)} </span>
    </div>`;
  }

  calcIndex(index: number) {
    return `${index + 1 < 10 ? '0' : ''}${index + 1}`;
  }

  calcProgress() {
    const pagination = this.data;

    return ((pagination.current_index + 1) / (pagination.max_index + 1)) * 100;
  }

  setCurrentIndex(index: number) {
    this.data.current_index = index;
  }

  override redraw(): this {
    const pagination = this.data;
    $(this.element()).find('.current_index')?.setInnerHtml(this.calcIndex(pagination.current_index));
    const progress = $(this.element()).find('.progress')?.element();
    if (progress) {
      progress.style.width = `${this.calcProgress()}%`;
    }

    return this;
  }
}

export class PlusBannerView extends View<PlusBannerProps> {
  informationView: PlusBannerInformationView;
  paginationView: PlusBannerPaginationView;
  navigationView: PlusBannerNavigationView;
  swiperView: SwiperView<BannerTypeS.PlusBannerData, typeof PlusBannerItemView>;
  current_index = 0;

  constructor(data: PlusBannerProps) {
    super(data);

    const current_index = 0;

    this.informationView = new PlusBannerInformationView(data.plus_crew);

    this.paginationView = new PlusBannerPaginationView({
      current_index,
      max_index: data.banners.length - 1,
    });
    this.navigationView = new PlusBannerNavigationView({
      prev_enable: data.banners.length > 1,
      next_enable: data.banners.length > 1,
    });
    this.swiperView = new SwiperView(
      {
        loop: false,
        speed: 800,
        rewind: true,
        allowTouchMove: data.banners.length > 1,
        autoplay:
          data.banners.length > 1
            ? {
                delay: 7000,
                pauseOnMouseEnter: true,
                waitForTransition: true,
                disableOnInteraction: false,
              }
            : false,
        init: false,
      },
      {
        swiper: klass.swiper,
        wrapper: klass.wrapper,
      },
      PlusBannerItemView,
      [...data.banners],
      data.plus_crew,
      data.banners[0]?.id,
      true,
    );
  }

  protected override template() {
    const is_mobile = !!getSharedData(this)?.is_mobile;

    return html` <div class="${klass.banner}">
      <div class="${klass.cover_image}">
        ${this.makeBackgroundImage()}
        <div class="${klass.dim}"></div>
      </div>
      <div class="${klass.banner_wrapper}">
        ${this.informationView}
        <div class="${klass.swiper_container}">
          ${this.swiperView}
          <div class="${klass.controller}">
            ${this.data.banners.length > 1 ? this.paginationView : ''}
            ${this.data.banners.length === 0 || is_mobile ? '' : this.navigationView}
          </div>
        </div>
      </div>
    </div>`;
  }

  makeBackgroundImage() {
    const is_mobile = !!getSharedData(this)?.is_mobile;
    const { pc_cover_image, mo_cover_image } = this.data.plus_crew;

    if (!is_mobile && pc_cover_image) {
      return html`<img
        class="${klass.image}"
        src="${getResizedUrl({
          url: pc_cover_image,
          format: 'webp',
          height: 1280,
        })}"
        alt="background cover image"
      />`;
    }

    if (is_mobile && mo_cover_image) {
      return html`<img
        class="${klass.image}"
        src="${getResizedUrl({
          url: mo_cover_image,
          format: 'webp',
          height: 1280,
        })}"
        alt="background cover image"
      />`;
    }

    return '';
  }

  protected override onRender() {
    if (this.swiperView.data.length > 1) {
      this.swiperView.swiper?.init();

      const io = new IntersectionObserver((els) => {
        els.forEach((entry) => {
          if (entry.isIntersecting) {
            this.swiperView.autoplayPlay();

            if (this.swiperView.active_view?.video && this.swiperView.active_view.slide_paused) {
              this.swiperView.active_view.video?.play();
              this.swiperView.active_view.slide_paused = false;
            }
          } else {
            this.swiperView.autoplayStop();

            if (this.swiperView.active_view?.video) {
              this.swiperView.active_view.video?.pause();
              this.swiperView.active_view.slide_paused = true;
            }
          }
        });
      });

      io.observe(this.element());
    }
  }

  @on(SwiperChangeEvent)
  swiperHandler(e: SwiperChangeEvent<PlusBannerItemView>) {
    const { current_index, active_view, views } = e.detail;
    this.paginationView.setCurrentIndex(current_index);
    this.paginationView.redraw();

    if (active_view.video && active_view.slide_paused) {
      active_view.video.play();
      active_view.slide_paused = false;
    }

    pipe(
      views,
      reject((view) => view.data.id === active_view.data.id || !view.video),
      each((view) => {
        if (!view.video?.paused()) {
          view.slide_paused = true;
        }

        view.video?.pause();
      }),
    );
  }

  @on(NavigateHandleEvent)
  navigationHandler(e: NavigateHandleEvent) {
    this.swiperView[e.detail.direction]();
  }

  @on(VideoActiveEvent)
  videoActiveHandler(e: VideoActiveEvent) {
    if (this.swiperView.swiper && e.detail.id === this.swiperView.active_view?.video?.data?.id) {
      e.detail.active ? this.swiperView.autoplayStop() : this.swiperView.autoplayPlay();
    }
  }
}
